import React, { useState, useEffect } from 'react';
import { object, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { isFlashSaleActive, getRemainingMs, formatTimeRemaining } from './flashSaleUtils';
import css from './FlashSaleBadge.module.css';

/**
 * FlashSaleBadge — live countdown badge for active flash sales.
 *
 * Shows discount % and time remaining with live countdown.
 * Disappears automatically when the sale ends.
 *
 * Usage (in ListingCard or ListingPage):
 *   {features.flashSales && flashSale && (
 *     <FlashSaleBadge flashSale={listing.attributes.publicData.flashSale} />
 *   )}
 *
 * @param {Object} props.flashSale — publicData.flashSale object
 */
const FlashSaleBadge = ({ flashSale, className }) => {
  const [remaining, setRemaining] = useState(() => getRemainingMs(flashSale));
  const [active, setActive] = useState(() => isFlashSaleActive(flashSale));

  useEffect(() => {
    if (!active) return;
    const interval = setInterval(() => {
      const ms = getRemainingMs(flashSale);
      setRemaining(ms);
      if (ms <= 0) {
        setActive(false);
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [flashSale, active]);

  if (!active) return null;

  return (
    <div className={classNames(css.root, className)}>
      <span className={css.lightning}>⚡</span>
      <span className={css.discount}>
        <FormattedMessage
          id="FlashSaleBadge.discount"
          values={{ percent: flashSale.discountPercent }}
        />
      </span>
      <span className={css.timer}>{formatTimeRemaining(remaining)}</span>
    </div>
  );
};

FlashSaleBadge.defaultProps = { className: null };
FlashSaleBadge.propTypes = { flashSale: object.isRequired, className: string };

export default FlashSaleBadge;
